/**
 * Epic 278 Phase 3 Slice D — richer new-activity decisions after sign-off.
 * Wires existing audited APIs only; never mutates a signed Scan.
 */

import { reportsApi, type SafetyDashboardRow } from '../../services/reportsApi';
import {
  buildCreateDraftPayloadFromHandoff,
  buildHandoffFromDashboardRow,
  buildSafetyScanWorkspacePath,
} from './safetyScanWorkspaceHandoff';
import { resolveSafetyCaseId, resolveSignedSafetyScanReportId } from '../../utils/acceptanceHierarchyNavigation';
import type { CustomerMetric } from '../customerMetrics';
import { metricUsesSignedScanWindow } from '../customerMetrics';

export type NewActivityDecisionKind =
  | 'acknowledge_continue_monitoring'
  | 'reopen_case'
  | 'follow_up_scan'
  | 'create_updated_scan';

export const NEW_ACTIVITY_DECISION_LABELS: Record<NewActivityDecisionKind, string> = {
  acknowledge_continue_monitoring: 'Acknowledge & continue monitoring',
  reopen_case: 'Reopen case',
  follow_up_scan: 'Start follow-up Scan',
  create_updated_scan: 'Create updated Scan for sign-off',
};

export const NEW_ACTIVITY_FROZEN_SCAN_COPY =
  'The Signed Safety Scan is frozen and remains unchanged. New activity is reviewed separately.';

export type NewActivityDecisionResult =
  | { ok: true; notice?: string; navigateTo?: string }
  | { ok: false; message: string };

function actorLabel(actor?: string | null): string {
  return String(actor || '').trim() || 'reviewer';
}

export function resolveNewActivityCount(row: SafetyDashboardRow): number | null {
  const extended = row as SafetyDashboardRow & {
    new_activity_count?: number | null;
    grouped_incident_count?: number | null;
  };
  const raw = extended.new_activity_count ?? extended.grouped_incident_count;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return Number(raw);
}

export function formatNewActivityCountSummary(args: {
  count?: number | null;
  semanticMetric?: CustomerMetric | null;
  signedScanBoundary?: string | null;
}): string | null {
  const raw = Number(args.count);
  if (!Number.isFinite(raw) || raw < 0) return null;
  const count = Math.trunc(raw);
  const noun = `${count.toLocaleString()} observation${count === 1 ? '' : 's'}`;
  if (metricUsesSignedScanWindow(args.semanticMetric, args.signedScanBoundary)) {
    return `${noun} since latest Signed Safety Scan`;
  }
  return `New activity detected: ${noun}`;
}

export function resolveFrozenSignedScanId(row: SafetyDashboardRow): string | null {
  return resolveSignedSafetyScanReportId({
    latest_finalized_report_id: row.latest_finalized_report_id || row.report_id,
    report_id: row.latest_finalized_report_id || row.report_id,
    report_status: row.latest_finalized_report_id ? 'finalized' : row.report_status,
  }) || null;
}

export function isActivityNewerThanAcknowledgment(
  activityAt?: string | null,
  acknowledgedThrough?: string | null,
): boolean {
  const activity = Date.parse(String(activityAt || '').trim());
  if (!Number.isFinite(activity)) return false;
  const acknowledged = Date.parse(String(acknowledgedThrough || '').trim());
  if (!Number.isFinite(acknowledged)) return true;
  return activity > acknowledged;
}

/**
 * Attention / case-register "new activity" membership after a signed Scan.
 * Acknowledgment through the latest activity watermark is authoritative: it clears
 * membership even when a stale API flag or active draft still looks like
 * follow-up work (drafts belong in awaiting sign-off, not new activity).
 */
export function resolveHasNewActivityAfterSignedScan(args: {
  hasSignedScan: boolean;
  hasNewActivitySinceReport?: boolean | null;
  workflowChangedSinceSignoff?: boolean | null;
  latestActivityAt?: string | null;
  signedAt?: string | null;
  acknowledgedThrough?: string | null;
}): boolean {
  if (!args.hasSignedScan) return false;
  const activityAt = String(args.latestActivityAt || '').trim();
  const acknowledgedThrough = String(args.acknowledgedThrough || '').trim();
  if (
    activityAt
    && acknowledgedThrough
    && !isActivityNewerThanAcknowledgment(activityAt, acknowledgedThrough)
  ) {
    return false;
  }
  if (args.hasNewActivitySinceReport || args.workflowChangedSinceSignoff) return true;
  const signedAt = String(args.signedAt || '').trim();
  if (!activityAt || !signedAt) return false;
  const activityMs = Date.parse(activityAt);
  const signedMs = Date.parse(signedAt);
  if (!Number.isFinite(activityMs) || !Number.isFinite(signedMs) || activityMs <= signedMs) {
    return false;
  }
  return isActivityNewerThanAcknowledgment(activityAt, acknowledgedThrough);
}

export function buildNewActivityAuditNote(args: {
  kind: NewActivityDecisionKind;
  actor: string;
  reviewerNote?: string | null;
  frozenSignedScanId?: string | null;
  newScanCreated?: boolean;
  newScanReportId?: string | null;
}): string {
  const note = String(args.reviewerNote || '').trim();
  return [
    `new_activity_decision=${args.kind}`,
    `actor=${actorLabel(args.actor)}`,
    `frozen_signed_scan_id=${String(args.frozenSignedScanId || '').trim() || 'none'}`,
    `new_scan_created=${args.newScanCreated ? 'true' : 'false'}`,
    args.newScanReportId ? `new_scan_report_id=${args.newScanReportId}` : null,
    note ? `reviewer_note=${note}` : null,
    'source_interface=safety_control_workspace_new_activity',
  ].filter(Boolean).join(' | ');
}

function activityWatermark(row: SafetyDashboardRow): string | null {
  const candidates = [
    row.latest_observation_at,
    row.source_workflow_updated_at,
  ]
    .map((value) => String(value || '').trim())
    .filter((value) => Number.isFinite(Date.parse(value)));
  if (!candidates.length) return null;
  return candidates.reduce((latest, value) => (
    Date.parse(value) > Date.parse(latest) ? value : latest
  ));
}

function decisionIdempotencyKey(args: {
  caseId: string;
  kind: NewActivityDecisionKind;
  row: SafetyDashboardRow;
}): string {
  const activity = activityWatermark(args.row) || 'unknown-activity';
  const frozen = resolveFrozenSignedScanId(args.row) || 'no-signed-scan';
  return `epic278:${args.caseId}:${args.kind}:${activity}:${frozen}`.slice(0, 255);
}

async function createOrResumeUpdatedScan(args: {
  row: SafetyDashboardRow;
  orgId?: string | null;
  siteId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
}): Promise<{ reportId: string; path: string }> {
  const workflowId = String(args.row.workflow_id || '').trim();
  if (!workflowId) {
    throw new Error('This Safety Case is missing a workflow id.');
  }
  const signedAt = String(args.row.report_finalized_at || args.row.latest_scan_signed_at || '').trim() || undefined;
  const handoff = buildHandoffFromDashboardRow(
    args.row,
    { orgId: args.orgId || undefined, siteId: args.siteId || undefined },
    {
      scanScopeStart: signedAt || args.reportFrom || undefined,
      scanScopeEnd: args.reportTo || new Date().toISOString(),
      signedScanAt: signedAt,
      signedScanScopeStart: args.row.report_scope_start || args.row.latest_scan_scope_start || undefined,
      signedScanScopeEnd: args.row.report_scope_end || args.row.latest_scan_scope_end || undefined,
      latestSignedScanId: resolveFrozenSignedScanId(args.row) || undefined,
      newActivityWindowStart: signedAt,
      isUpdatedScan: true,
    },
  );
  const openPath = async (reportId: string): Promise<string> => {
    const path = buildSafetyScanWorkspacePath(handoff, reportId);
    if (!path) {
      throw new Error('Unable to open this Safety Scan without a report id.');
    }
    return path;
  };

  if (args.row.active_draft_report_id) {
    const reportId = String(args.row.active_draft_report_id).trim();
    return { reportId, path: await openPath(reportId) };
  }
  const response = await reportsApi.createOrResumeSafetyReportDraft(
    workflowId,
    buildCreateDraftPayloadFromHandoff(handoff),
  );
  const reportId = String(response.report?.report_id || '').trim();
  if (!reportId) {
    throw new Error('Unable to create or resume a Safety Scan draft.');
  }
  return { reportId, path: await openPath(reportId) };
}

export async function submitNewActivityDecision(args: {
  row: SafetyDashboardRow;
  kind: NewActivityDecisionKind;
  actor: string;
  reviewerNote?: string | null;
  orgId?: string | null;
  siteId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  liveCasePath?: string | null;
}): Promise<NewActivityDecisionResult> {
  const caseId = resolveSafetyCaseId(args.row);
  const incidentId = String(args.row.representative_incident_id || '').trim();
  const frozenSignedScanId = resolveFrozenSignedScanId(args.row);
  const note = String(args.reviewerNote || '').trim();

  try {
    if (args.kind === 'acknowledge_continue_monitoring') {
      if (!caseId) {
        return { ok: false, message: 'A Safety Case id is required to acknowledge new activity.' };
      }
      if (!note) {
        return { ok: false, message: 'Add a short reviewer note before acknowledging.' };
      }
      const acknowledgedThrough = activityWatermark(args.row);
      if (!acknowledgedThrough) {
        return { ok: false, message: 'The latest activity timestamp is unavailable; acknowledgment was not recorded.' };
      }
      await reportsApi.recordNewActivityDecision(caseId, {
        decision_kind: args.kind,
        idempotency_key: decisionIdempotencyKey({ caseId, kind: args.kind, row: args.row }),
        reviewer_note: note,
        representative_incident_id: incidentId || undefined,
        acknowledged_through: acknowledgedThrough,
        frozen_signed_scan_id: frozenSignedScanId,
      });
      return {
        ok: true,
        notice: 'New activity acknowledged. Monitoring continues; signed Scan unchanged.',
      };
    }

    if (args.kind === 'reopen_case') {
      if (!caseId) {
        return { ok: false, message: 'A Safety Case id is required to reopen.' };
      }
      if (!note) {
        return { ok: false, message: 'Add a short reviewer note before reopening.' };
      }
      const navigateTo = String(args.liveCasePath || '').trim();
      if (!navigateTo) {
        return { ok: false, message: 'Unable to open the Safety Case; reopen was not recorded.' };
      }
      await reportsApi.recordNewActivityDecision(caseId, {
        decision_kind: args.kind,
        idempotency_key: decisionIdempotencyKey({ caseId, kind: args.kind, row: args.row }),
        reviewer_note: note,
        representative_incident_id: incidentId || undefined,
        acknowledged_through: activityWatermark(args.row),
        frozen_signed_scan_id: frozenSignedScanId,
      });
      return {
        ok: true,
        notice: 'Reopen recorded. Opening the Safety Case for stage updates.',
        navigateTo,
      };
    }

    if (args.kind === 'follow_up_scan' || args.kind === 'create_updated_scan') {
      if (!caseId) {
        return { ok: false, message: 'A Safety Case id is required to start a follow-up Scan.' };
      }
      await reportsApi.recordNewActivityDecision(caseId, {
        decision_kind: args.kind,
        idempotency_key: decisionIdempotencyKey({ caseId, kind: args.kind, row: args.row }),
        reviewer_note: note || undefined,
        representative_incident_id: incidentId || undefined,
        acknowledged_through: activityWatermark(args.row),
        frozen_signed_scan_id: frozenSignedScanId,
      });
      const created = await createOrResumeUpdatedScan({
        row: args.row,
        orgId: args.orgId,
        siteId: args.siteId,
        reportFrom: args.reportFrom,
        reportTo: args.reportTo,
      });
      return {
        ok: true,
        notice: args.kind === 'follow_up_scan'
          ? 'Follow-up Scan draft ready. Signed Scan remains frozen.'
          : 'Updated Safety Scan draft ready for review and sign-off. Signed Scan remains frozen.',
        navigateTo: created.path,
      };
    }

    return { ok: false, message: 'Unknown new-activity decision.' };
  } catch (err: unknown) {
    const message = String(
      (err as { message?: string })?.message
      || 'Unable to record the new-activity decision.',
    ).trim();
    return { ok: false, message };
  }
}
